import React from "react";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";

import { Button, Card, PageHeader, showSnackbar, Table } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { Language, usePermission } from "imports/Utils";

import { FormattedDate } from "utils/DateUtils";

import { GetProductCategoriesBySearchDocument, GetProductCategoriesBySearchQuery } from "__generated__/view/productcategories/graphql/queries.generated";
import { DeleteProductCategoryDocument } from "__generated__/view/productcategories/graphql/mutations.generated";
import { ChildPropsFromPagination } from "types/commons";

type ProductCategoryFromQuery = ChildPropsFromPagination<GetProductCategoriesBySearchQuery, "productCategoriesBySearch">

const ProductCategories = () => {

	const confirm = useConfirm();
	const navigate = useNavigate();
	const { isReadOnly } = usePermission();

	const client = useApolloClient();

	const { tableProps, refetchAfterDelete } = useBackendTable({
		id: TableId.ProductCategories,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetProductCategoriesBySearchDocument,
					variables: {
						name: filter.name,
						pagination: {
							...pagination,
							sortList: sort,
						},
					},
				});

				callback({
					content: result.data.productCategoriesBySearch.content ?? [],
					totalResults: result.data.productCategoriesBySearch.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const [ deleteProductCategory ] = useMutation(DeleteProductCategoryDocument, {
		onCompleted: () => {
			refetchAfterDelete();
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
		},
	});

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("PRODUCT_CATEGORIES")}
					<Helmet title={intl.formatMessage({ id: "PRODUCT_CATEGORIES" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => navigate("/view/productcategories/create")}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					columns={[ {
						id: "name",
						title: intl.formatMessage({ id: "NAME" }),
						filterComponent: "TEXT",
						sortable: true,
					}, {
						id: "createdOn",
						title: intl.formatMessage({ id: "CREATED" }),
						render: (row: ProductCategoryFromQuery) => <FormattedDate date={row.createdOn} format="ll" />,
						sortable: true,
						defaultVisible: false,
					} ]}
					rowActions={isReadOnly ? undefined : [ {
						icon: <EditIcon />,
						onClick: (row) => navigate(`/view/productcategories/${row.id}/edit`),
					}, {
						icon: <DeleteIcon />,
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deleteProductCategory({
									variables: {
										id: row.id,
									},
								});
							});
						},
					} ]} />
			</Card>
		</React.Fragment>
	);
};

export default ProductCategories;
